import { FeeScheduleCategoryType } from "_shared/fee-schedules/categories/types/fee-schedule-category-type";
import WorkVersion from "work/work-agreements/versions/work-version";
import WorkVersionChip from "work/work-agreements/versions/_views/work-version-chip";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import FormControlLabel from "@mui/material/FormControlLabel";
import Link from "@mui/material/Link";
import { styled } from "@mui/material/styles";
import { visuallyHidden } from "@mui/utils";
import Switch from "@mui/material/Switch";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { SESSION_RECORDING_MASK_CLASS } from "_shared/session-recording/session-recording-privacy";
import { HttpStatusCode } from "axios";
import { NotAuthorized } from "_shared/_errors/_views/not-authorized";
import Loader from "_shared/_views/loading/loader";
import AHDate from "_shared/dates/date";
import Fee from "_shared/fee-schedules/categories/fees/fee";
import Guid from "_shared/guids/guid";
import { formatCurrency } from "_shared/money/currencies/currency";
import { Fragment, ReactNode, useMemo } from "react";
import { Link as RouterLink, useParams, useSearchParams } from "react-router";
import Session from "users/__session/session";
import { useSession } from "users/__session/session-context";
import { Masked } from "work/masked-fields";
import { useGetProposalComparisonQuery } from "work/proposals/comparison/_api/proposal-comparison-api";
import ComparedPersonName, { ComparedPeople } from "work/proposals/comparison/_views/compared-person-name";
import ProposalComparison, {
  actionsOfferedTo,
  ComparedFee,
  ComparedRate,
  ComparedTeamMember,
  ComparedTerms,
  FeeComparisonCell,
  ProposalComparisonColumn,
  vendorTeamSignature,
} from "work/proposals/comparison/proposal-comparison";
import ProposalHiredTerms from "work/proposals/hired-terms/proposal-hired-terms";
import { MaskedProposalField, maskedProposalFieldNotice } from "work/proposals/_views/_shared/masked-field-notice";
import { proposalListDestination } from "work/proposals/_views/proposal-list-destination";
import Proposal from "work/proposals/proposal";
import VendorTeamLink from "work/work-agreements/teams/vendor-team/_views/vendor-team-link";
import { DateCell } from "_shared/_views/tables/date-cells";
import { formatDate } from "_shared/formatting/dates";

/** A refused read: the group exists and this reader may not compare it (403). Never "not found". */
export const PROPOSAL_COMPARISON_REFUSED = "You do not have permission to compare the proposals of this request.";
/** No live proposal carries the id (404). */
export const PROPOSAL_COMPARISON_ABSENT = "No proposals answer this request.";
/** What a cell of a proposal the reader may not read says. */
export const UNAVAILABLE_PROPOSAL_TEXT = "Not available to you";
/** What a fee cell says when the server withheld one category's rate. */
export const MASKED_RATE_TEXT = "You do not have permission to view this rate.";
/** What a fee cell says when the server withheld who bills a category. */
export const MASKED_TEAM_MEMBER_TEXT = "You do not have permission to view who bills this.";
/** The marker on a row whose readable values are not all the same. */
export const DIFFERS_TEXT = "Differs";
/** What the table says when "Only differences" leaves no row. */
export const NO_DIFFERENCES_TEXT = "These proposals do not differ in any term you can see.";
/** The query parameters the page's two switches are held in, so a shared link keeps them (#6633). */
export const ONLY_DIFFERENCES_PARAMETER = "differences";
export const ARCHIVED_PARAMETER = "archived";
/** What the page says when hiding archived proposals leaves no proposal the reader may read. */
export const ARCHIVED_HIDE_EVERY_READABLE_TEXT =
  "Every proposal you may read in this request is archived. Include archived to see them.";

const TERM_COLUMN_WIDTH = 180;
const NARROW_TERM_COLUMN_WIDTH = 140;
const FIRM_COLUMN_MIN_WIDTH = 220;

const Page = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  minWidth: 0,
  overflowY: "auto",
  padding: theme.spacing(2),
  width: "100%",
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1),
  },
}));

/**
 * A fixed layout: the term column keeps its width and the firms share the rest equally, so one long
 * name cannot push its column wider than the others. Words wrap between words. Only a word wider than
 * its column, such as a long hyphenated name, breaks inside itself, so nothing is cut
 * off. Every row is at least one line of controls high.
 */
const ComparisonTable = styled(Table)(() => ({
  tableLayout: "fixed",
  width: "100%",
  "& th, & td": {
    height: 48,
    hyphens: "manual",
    overflowWrap: "break-word",
    verticalAlign: "top",
    wordBreak: "normal",
  },
}));

const TermColumn = styled("col")(({ theme }) => ({
  width: TERM_COLUMN_WIDTH,
  [theme.breakpoints.down("sm")]: {
    width: NARROW_TERM_COLUMN_WIDTH,
  },
}));

const TermCell = styled(TableCell, { shouldForwardProp: (prop) => prop !== "differs" })<{ differs?: boolean }>(
  ({ theme, differs }) => ({
    backgroundColor: theme.palette.background.paper,
    // A side rule marks a row whose values differ, beside the marker and the weight of its cells.
    borderLeft: `3px solid ${differs ? theme.palette.warning.main : "transparent"}`,
    fontWeight: 600,
    left: 0,
    position: "sticky",
    zIndex: 1,
  }),
);

const FirmCell = styled(TableCell, { shouldForwardProp: (prop) => prop !== "differs" })<{ differs?: boolean }>(
  ({ differs }) => ({
    fontWeight: differs ? 600 : undefined,
  }),
);

const SectionCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: theme.palette.action.hover,
  fontWeight: 600,
}));

const Muted = styled("span")(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontWeight: 400,
}));

/** One term the comparison shows: how a column's value reads, and the value it is compared by. */
type TermDefinition = {
  term: string;
  /** A comparable value, or undefined when the server withheld it from this reader. */
  signature: (proposal: Proposal, terms: ComparedTerms) => string | undefined;
  render: (proposal: Proposal, terms: ComparedTerms) => ReactNode;
};

/**
 * `/proposals/compare/:rfpId`: the proposals one request produced, one column per firm (#581).
 *
 * Only the client side reaches it. The server decides who may compare and masks each field per
 * firm and per reader; this page shows what came back and hides nothing on its own. A firm whose
 * proposal the reader may not read keeps its column, marked unavailable.
 *
 * The rows whose values differ are marked, and "Only differences" hides the rest. "Include archived"
 * shows or hides the proposals that are over. Both are held in the address (#6633).
 */
export default function ProposalComparisonPage() {
  const { rfpId } = useParams();
  const isValidId = Boolean(rfpId && Guid.isValid(rfpId));
  const session = useSession();
  const query = useGetProposalComparisonQuery({ rfpId: rfpId ?? "" }, { skip: !isValidId });
  const currentUser = session.user;
  // `currentData`, not `data`: RTK Query keeps the previous request's result in `data` while the
  // next one loads, and this page must never show one request's firms under another's address.
  const current = query.currentData;
  const comparison = useMemo(() => {
    if (!current || !currentUser) return undefined;
    const hiredTerms = new Map<string, ProposalHiredTerms>();
    for (const json of current.found) {
      const terms = ProposalHiredTerms.fromJSON(json.hiredTerms);
      if (terms && json.id) hiredTerms.set(json.id, terms);
    }
    return ProposalComparison.fromResult({
      found: current.found.flatMap((json) => {
        const proposal = Proposal.fromJSON({ json, currentUser });
        return proposal ? [proposal] : [];
      }),
      unavailable: current.unavailable,
      hiredTerms,
    });
  }, [current, currentUser]);

  if (!isValidId) return <ComparisonMessage message={PROPOSAL_COMPARISON_ABSENT} />;
  // The error is the current argument's: RTK Query does not carry one over to the next argument.
  if (query.error && !query.isFetching) {
    const status = "status" in query.error ? query.error.status : undefined;
    if (status === HttpStatusCode.Forbidden) return <NotAuthorized message={PROPOSAL_COMPARISON_REFUSED} />;
    if (status === HttpStatusCode.NotFound) return <ComparisonMessage message={PROPOSAL_COMPARISON_ABSENT} />;
    return (
      <ComparisonMessage
        message="We could not load these proposals."
        onRetry={() => void query.refetch()}
      />
    );
  }
  if (!comparison) return <Loader />;
  return (
    <ComparedPeople userIds={comparison.namedPeople}>
      <LoadedProposalComparison comparison={comparison} session={session} />
    </ComparedPeople>
  );
}

function ComparisonMessage(props: Readonly<{ message: string; onRetry?: () => void }>) {
  const { message, onRetry } = props;
  return (
    <Page>
      <BackToProposals />
      <Alert
        severity="error"
        action={onRetry ? <Button color="inherit" size="small" onClick={onRetry}>Retry</Button> : undefined}
      >
        {message}
      </Alert>
    </Page>
  );
}

function BackToProposals() {
  return (
    <div>
      <Button component={RouterLink} to="/proposals" startIcon={<ArrowBackIcon />}>
        Proposals
      </Button>
    </div>
  );
}

function LoadedProposalComparison(props: Readonly<{ comparison: ProposalComparison; session: Session }>) {
  const { comparison: wholeComparison, session } = props;
  const [searchParams, setSearchParams] = useSearchParams();
  const onlyDifferences = searchParams.get(ONLY_DIFFERENCES_PARAMETER) === "only";
  // On unless the address says otherwise: an archived proposal in the group is there for a reason,
  // and hiding it unasked would hide why (#6633).
  const includeArchived = searchParams.get(ARCHIVED_PARAMETER) !== "exclude";
  const comparison = includeArchived ? wholeComparison : wholeComparison.withoutArchived();
  const columns = comparison.columns;
  const feeRows = comparison.feeRows;
  const currencies = comparison.currencies;
  const unavailableCount = comparison.unavailableCount;
  const userId = session.user?.id;

  function setParameter(name: string, value: string | undefined) {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (value === undefined) next.delete(name);
        else next.set(name, value);
        return next;
      },
      { replace: true },
    );
  }

  const leadingTerms: TermDefinition[] = [
    {
      term: "Status",
      signature: (proposal) => proposal.displayStatus,
      render: (proposal) => <StatusCell proposal={proposal} userId={userId} />,
    },
    {
      term: "Vendor team",
      signature: (_proposal, terms) => vendorTeamSignature(terms.vendorTeam),
      render: (proposal, terms) =>
        Masked.isMasked(terms.vendorTeam) ? (
          <MaskedValue text={maskedProposalFieldNotice(MaskedProposalField.Team)} />
        ) : (
          <VendorTeamLink displayMode="full" proposal={proposal} session={session} team={terms.vendorTeam} />
        ),
    },
  ];
  const trailingTerms: TermDefinition[] = [
    {
      term: "Discount",
      signature: (proposal) => (Masked.isMasked(proposal.discount) ? undefined : proposal.discount.toString()),
      render: (proposal) =>
        Masked.isMasked(proposal.discount) ? (
          <MaskedValue text={maskedProposalFieldNotice(MaskedProposalField.Discount)} />
        ) : (
          proposal.discount.toString()
        ),
    },
    {
      term: "Negotiable",
      signature: (proposal) => String(proposal.negotiable.value),
      render: (proposal) => (proposal.negotiable.value ? "Yes" : "No"),
    },
    {
      term: "Response due",
      signature: (proposal) => dateSignature(proposal.responseDueBy),
      render: (proposal) => <DateValue date={proposal.responseDueBy} />,
    },
    {
      term: "Start",
      signature: (proposal) => dateSignature(proposal.startDate),
      render: (proposal) => <DateValue date={proposal.startDate} />,
    },
    {
      term: "End",
      signature: (proposal) => dateSignature(proposal.endDate),
      render: (proposal) => <DateValue date={proposal.endDate} />,
    },
  ];
  const shownLeadingTerms = leadingTerms.filter((term) => !onlyDifferences || comparison.differs(term.signature));
  const shownTrailingTerms = trailingTerms.filter((term) => !onlyDifferences || comparison.differs(term.signature));
  const shownFeeRows = feeRows.filter((row) => !onlyDifferences || row.differs);
  const nothingShown = shownLeadingTerms.length + shownTrailingTerms.length + shownFeeRows.length === 0;

  return (
    <Page>
      <BackToProposals />
      <Typography variant="h4" component="h1">
        Compare proposals
      </Typography>
      <Typography color="text.secondary">
        {wholeComparison.columns.length}{" "}
        {wholeComparison.columns.length === 1 ? "proposal answers" : "proposals answer"} this request. Each firm
        sees only its own proposal.
      </Typography>
      <Box sx={{ columnGap: 3, display: "flex", flexWrap: "wrap" }}>
        <FormControlLabel
          control={
            <Switch
              checked={onlyDifferences}
              onChange={(event) => setParameter(ONLY_DIFFERENCES_PARAMETER, event.target.checked ? "only" : undefined)}
            />
          }
          label="Only differences"
        />
        {wholeComparison.hasArchived && (
          <FormControlLabel
            control={
              <Switch
                checked={includeArchived}
                onChange={(event) => setParameter(ARCHIVED_PARAMETER, event.target.checked ? undefined : "exclude")}
              />
            }
            label="Include archived"
          />
        )}
      </Box>
      {currencies.length > 1 && (
        <Alert severity="info">
          Rates are quoted in more than one currency ({currencies.join(", ")}). Each rate is shown as the firm
          quoted it. Nothing is converted, added up or ranked.
        </Alert>
      )}
      {unavailableCount > 0 && (
        <Alert severity="info">
          You may not read {unavailableCount} of these proposals. A firm&apos;s answer shows here once it is sent to
          you.
        </Alert>
      )}
      {wholeComparison.hasArchived && !comparison.hasReadableColumn ? (
        <Alert severity="info">{ARCHIVED_HIDE_EVERY_READABLE_TEXT}</Alert>
      ) : (
        <TableContainer sx={{ overflowX: "auto" }}>
          <ComparisonTable
            aria-label="Proposal comparison"
            size="small"
            sx={{ minWidth: TERM_COLUMN_WIDTH + columns.length * FIRM_COLUMN_MIN_WIDTH }}
          >
            <colgroup>
              <TermColumn />
              {columns.map((column) => (
                <col key={column.id} />
              ))}
            </colgroup>
            {/* Each column is headed by a proposal's name, so session replays mask the header (#6330). */}
            <TableHead className={SESSION_RECORDING_MASK_CLASS}>
              <TableRow>
                <TermCell component="th" scope="col">
                  Term
                </TermCell>
                {columns.map((column) => (
                  <FirmCell key={column.id} component="th" scope="col">
                    <ColumnHeading column={column} session={session} />
                  </FirmCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {shownLeadingTerms.map((term) => (
                <TermRow
                  key={term.term}
                  definition={term}
                  columns={columns}
                  differs={comparison.differs(term.signature)}
                />
              ))}
              {shownFeeRows.length > 0 && (
                <TableRow>
                  <SectionCell component="th" scope="rowgroup" colSpan={columns.length + 1}>
                    Fee schedule
                  </SectionCell>
                </TableRow>
              )}
              {shownFeeRows.map((row) => (
                <TableRow key={row.names.join("|").toLocaleLowerCase()} data-differs={row.differs || undefined}>
                  <TermCell component="th" scope="row" differs={row.differs}>
                    <CategoryName names={row.names} />
                    {row.differs && <DiffersMarker />}
                  </TermCell>
                  {row.cells.map((cell, index) => (
                    <FirmCell key={columns[index].id} differs={row.differs}>
                      <FeeCell cell={cell} rowName={row.names[row.names.length - 1]} />
                    </FirmCell>
                  ))}
                </TableRow>
              ))}
              {!onlyDifferences && feeRows.length === 0 && !comparison.hasMaskedFeeSchedule && (
                <TableRow>
                  <TableCell colSpan={columns.length + 1}>
                    <Muted>No firm has quoted a fee yet.</Muted>
                  </TableCell>
                </TableRow>
              )}
              {!onlyDifferences && feeRows.length === 0 && comparison.hasMaskedFeeSchedule && (
                <TermRow definition={MASKED_FEES_TERM} columns={columns} differs={false} />
              )}
              {shownTrailingTerms.map((term) => (
                <TermRow
                  key={term.term}
                  definition={term}
                  columns={columns}
                  differs={comparison.differs(term.signature)}
                />
              ))}
              {nothingShown && (
                <TableRow>
                  <TableCell colSpan={columns.length + 1}>
                    <Muted>{NO_DIFFERENCES_TEXT}</Muted>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </ComparisonTable>
        </TableContainer>
      )}
    </Page>
  );
}

/** The one fee row a comparison with no visible category has, when a firm's schedule is withheld. */
const MASKED_FEES_TERM: TermDefinition = {
  term: "Fees",
  signature: () => undefined,
  render: (_proposal, terms) =>
    Masked.isMasked(terms.feeSchedule) ? (
      <MaskedValue text={maskedProposalFieldNotice(MaskedProposalField.FeeSchedule)} />
    ) : (
      <Muted>No fees quoted</Muted>
    ),
};

/** The day a date falls on, so two dates compare equal when the row would draw them alike. */
function dateSignature(date: AHDate | null | undefined): string {
  return date?.isValid ? String(date.startOf("day").valueOf()) : "";
}

function ColumnHeading(props: Readonly<{ column: ProposalComparisonColumn; session: Session }>) {
  const { column, session } = props;
  if (column.kind === "unavailable") {
    return <Muted>A proposal you may not read</Muted>;
  }
  const { proposal, terms } = column;
  const name = proposal.name?.value ?? "Untitled proposal";
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
      {/* A change order keeps the name of the work it revises, so its version tells the columns apart (#6636). */}
      <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", rowGap: 0.25 }}>
        {proposal.id ? (
          <Link
            component={RouterLink}
            to={proposalListDestination(proposal, {
              userId: session.user?.id,
              isRestrictedOnboarding: session.isRestrictedOnboarding,
            })}
          >
            {name}
          </Link>
        ) : (
          <span>{name}</span>
        )}
        <WorkVersionChip version={WorkVersion.of(proposal.workAgreement)} />
      </Box>
      <TermsSource terms={terms} />
    </Box>
  );
}

/** Says when a column's team and fees are not the proposal as it stands: as hired, or only today's. */
function TermsSource(props: Readonly<{ terms: ComparedTerms }>) {
  const { terms } = props;
  const hiredOn = formatDate(terms.hiredAt);
  if (terms.source === "hired") return <Muted>Team and fees as hired on {hiredOn}</Muted>;
  if (terms.source === "liveAfterHire") {
    return <Muted>Hired on {hiredOn}. Team and fees as they stand today: the hired ones were not kept.</Muted>;
  }
  return null;
}

/** One term, with each firm's value. A firm the reader may not read says so in its cell. */
function TermRow(
  props: Readonly<{
    definition: TermDefinition;
    columns: readonly ProposalComparisonColumn[];
    differs: boolean;
  }>,
) {
  const { definition, columns, differs } = props;
  return (
    <TableRow data-differs={differs || undefined}>
      <TermCell component="th" scope="row" differs={differs}>
        {definition.term}
        {differs && <DiffersMarker />}
      </TermCell>
      {columns.map((column) => (
        <FirmCell key={column.id} differs={differs}>
          {column.kind === "proposal" ? (
            definition.render(column.proposal, column.terms)
          ) : (
            <Muted>{UNAVAILABLE_PROPOSAL_TEXT}</Muted>
          )}
        </FirmCell>
      ))}
    </TableRow>
  );
}

function DiffersMarker() {
  return (
    <Chip
      label={DIFFERS_TEXT}
      size="small"
      color="warning"
      variant="outlined"
      sx={{ display: "flex", mt: 0.5, width: "fit-content" }}
    />
  );
}

/** A category's name. A renamed one shows what it was called, struck through, before its new name. */
function CategoryName(props: Readonly<{ names: string[] }>) {
  const { names } = props;
  const current = names[names.length - 1];
  return (
    <span>
      {names.slice(0, -1).map((name) => (
        <Fragment key={name}>
          {/* A strike-through is not read aloud, so a screen reader is told in words. */}
          <span style={visuallyHidden}>Previously </span>
          <Box component="s" sx={{ color: "text.secondary", fontWeight: 400 }}>
            {name}
          </Box>{" "}
          <span style={visuallyHidden}>now </span>
        </Fragment>
      ))}
      {current}
    </span>
  );
}

function StatusCell(props: Readonly<{ proposal: Proposal; userId: Guid | undefined }>) {
  const { proposal, userId } = props;
  const actions = actionsOfferedTo(proposal, userId);
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      <span>{proposal.displayStatus}</span>
      <Muted>{actions.length > 0 ? `You can: ${actions.join(", ")}` : "Nothing for you to do"}</Muted>
    </Box>
  );
}

function FeeCell(props: Readonly<{ cell: FeeComparisonCell; rowName: string }>) {
  const { cell, rowName } = props;
  switch (cell.kind) {
    case "masked":
      return <MaskedValue text={maskedProposalFieldNotice(MaskedProposalField.FeeSchedule)} />;
    case "unavailable":
      return <Muted>{UNAVAILABLE_PROPOSAL_TEXT}</Muted>;
    case "absent":
      return <Muted>Not quoted</Muted>;
    case "fees":
      return (
        <Box component="ul" sx={{ display: "flex", flexDirection: "column", gap: 1, listStyle: "none", m: 0, p: 0 }}>
          {cell.fees.map((fee, index) => (
            // A firm may quote one category more than once, for different people; the order is theirs.
            <li key={index}>
              <FeeValue fee={fee} rowName={rowName} />
            </li>
          ))}
        </Box>
      );
  }
}

/**
 * Every attribute of one quoted category: its rate and unit, who bills it, and whether it is an
 * expense. A fee is what a category is unless it says otherwise, so only an expense is named.
 */
function FeeValue(props: Readonly<{ fee: ComparedFee; rowName: string }>) {
  const { fee, rowName } = props;
  const isRenamedHere = fee.name.toLocaleLowerCase() !== rowName.toLocaleLowerCase();
  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <RateValue rate={fee.rate} />
      {isRenamedHere && <Muted>Named {fee.name}</Muted>}
      <TeamMemberValue teamMember={fee.teamMember} />
      {fee.type === FeeScheduleCategoryType.Expense && <Muted>Expense</Muted>}
    </Box>
  );
}

function RateValue(props: Readonly<{ rate: ComparedRate }>) {
  const { rate } = props;
  if (rate.kind === "currencyMasked") return <MaskedValue text={MASKED_RATE_TEXT} />;
  if (rate.kind === "deferred") return <Muted>Fee not set</Muted>;
  return <span>{describeFee(rate.fee)}</span>;
}

function TeamMemberValue(props: Readonly<{ teamMember: ComparedTeamMember }>) {
  const { teamMember } = props;
  if (teamMember.kind === "masked") return <MaskedValue text={MASKED_TEAM_MEMBER_TEXT} />;
  // Most categories name nobody; saying so on every row would bury the ones that do.
  if (teamMember.kind === "none") return null;
  return (
    <Muted>
      Billed by <ComparedPersonName userId={teamMember.member.userId} />
    </Muted>
  );
}

/** A rate with its own currency and unit, as the firm quoted it: "$300.00 USD per hour". */
export function describeFee(fee: Fee): string {
  const amount = `${formatCurrency(fee.rate)} ${fee.rate.currency.toString()}`;
  return fee.unit ? `${amount} per ${fee.unit}` : `${amount} fixed`;
}

function MaskedValue(props: Readonly<{ text: string }>) {
  const { text } = props;
  return (
    <Box
      component="span"
      sx={{ alignItems: "center", color: "text.secondary", display: "inline-flex", fontWeight: 400, gap: 0.5 }}
    >
      <LockOutlinedIcon fontSize="inherit" aria-hidden />
      <span>{text}</span>
    </Box>
  );
}

function DateValue(props: Readonly<{ date: AHDate | null | undefined }>) {
  const { date } = props;
  return <DateCell value={date} />;
}
